import { availableLanguagesForUser, manageCarrierServiceTypes } from '@/common/constants'
import {
  validateDestinationRegions,
  validateSourceRegions,
} from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/helpers'

import { shouldShowExtraFeeAdditionalTransaction } from './CostSection/FeeConfiguration/helpers'
import { enabledForCards, errorTypes, feeTypes, locationListTypeValues, returnMethods } from './constants'

export const getAvailableLanguages = supportedLanguages => {
  return availableLanguagesForUser.filter(language => supportedLanguages?.includes(language))
}

export const getVisibleEnabledForCards = allowsUnifiedFlow => {
  return enabledForCards.filter(card => !card.requiresUnifiedFlow || allowsUnifiedFlow)
}

export const filteredNames = (deliveryMethodName, supportedLanguages) => {
  if (!deliveryMethodName) return {}
  return Object.keys(deliveryMethodName)
    .filter(key => supportedLanguages.includes(key))
    .reduce((obj, key) => {
      obj[key] = deliveryMethodName[key]
      return obj
    }, {})
}

const validateAllowedFor = deliveryMethod => {
  if (!deliveryMethod.enabled_for_exchanges && !deliveryMethod.enabled_for_refunds && !deliveryMethod.enabled_for_mixed)
    return [{ type: errorTypes.allowedEmpty }]
  return []
}

const validateRegions = deliveryMethod => {
  if (!deliveryMethod?.regions?.length) return [{ type: errorTypes.regionsEmpty }]
  return []
}

const validateExtraFee = deliveryMethod => {
  if (deliveryMethod.extra_fee === '') return [{ type: errorTypes.extraFeeEmpty }]
  return []
}

const validateIncludedItems = deliveryMethod => {
  if (deliveryMethod.extra_fee_type !== feeTypes.perReturnedItem) return []

  const errors = []
  const hasAdditionalTransactionFee = shouldShowExtraFeeAdditionalTransaction({
    extraFeeType: deliveryMethod.extra_fee_type,
    extraAdditionalFee: deliveryMethod.extra_fee_additional_transaction,
    baseExtraAdditionalFee: deliveryMethod.base_extra_fee_additional_transaction,
  })

  if (deliveryMethod.extra_fee_included_items < 1) errors.push({ type: errorTypes.invalidIncludedItems })
  if (hasAdditionalTransactionFee && deliveryMethod.extra_fee_included_items_additional_transaction < 1)
    errors.push({ type: errorTypes.invalidIncludedItemsAdditionalTransaction })

  return errors
}

const validateCustomNames = (deliveryMethod, supportedLanguages) => {
  const requiredNames = filteredNames(deliveryMethod?.name, supportedLanguages)
  if (supportedLanguages.some(lang => !requiredNames[lang])) return [{ type: errorTypes.customNamesEmpty }]
  return []
}

const validateLocation = deliveryMethod => {
  const requiresLocation = [
    returnMethods.customerdelivers,
    returnMethods.customerpickup,
    returnMethods.pickuppoint,
  ].includes(deliveryMethod.type)

  if (requiresLocation && !deliveryMethod.location_id && !deliveryMethod?.location?.id)
    return [{ type: errorTypes.locationIdEmpty }]
  return []
}

const validateCarrier = deliveryMethod => {
  if (deliveryMethod.type !== returnMethods.pickuppoint) return []
  if (!deliveryMethod?.carrier?.id && !deliveryMethod?.carrier_service?.id) return [{ type: errorTypes.carrierEmpty }]
  return []
}

const validateInternalName = deliveryMethod => {
  if (!deliveryMethod.internal_name || deliveryMethod.internal_name === '')
    return [{ type: errorTypes.internalNameEmpty }]
  return []
}

const validateCarrierServiceRegions = (deliveryMethod, regions) => {
  if (!deliveryMethod?.carrier_service) return []

  const errors = []
  const isValidSourceRegions = validateSourceRegions({
    sourceRegionIds: deliveryMethod?.carrier_service?.source_region_ids,
    methodRegions: deliveryMethod?.regions,
    allRegions: regions,
  })
  const isValidDestinationRegions = validateDestinationRegions({
    destinationRegionIds: deliveryMethod?.carrier_service?.destination_region_ids,
    location: deliveryMethod?.location,
    allRegions: regions,
  })

  if (!isValidSourceRegions) errors.push({ type: errorTypes.invalidRegionForCarrierService })
  if (!isValidDestinationRegions) errors.push({ type: errorTypes.invalidLocationForCarrierService })
  return errors
}

export const validateDeliveryMethod = ({ deliveryMethod, supportedLanguages, regions }) => [
  ...validateAllowedFor(deliveryMethod),
  ...validateRegions(deliveryMethod),
  ...validateExtraFee(deliveryMethod),
  ...validateIncludedItems(deliveryMethod),
  ...validateCustomNames(deliveryMethod, supportedLanguages),
  ...validateLocation(deliveryMethod),
  ...validateCarrier(deliveryMethod),
  ...validateInternalName(deliveryMethod),
  ...validateCarrierServiceRegions(deliveryMethod, regions),
]

export const getReturnProtectionRegionIds = returnProtection => {
  if (!returnProtection?.regions?.length) return []

  return returnProtection.regions.map(region => region.region_id)
}

export const getRegionsWithReturnProtectionDisabled = ({ regions, returnProtectionRegionIds, reason }) => {
  if (!regions?.length || !returnProtectionRegionIds?.length) return regions

  return regions.map(region => {
    if (!returnProtectionRegionIds.includes(region.id)) return region

    return { ...region, disabled: true, disabledReason: reason }
  })
}

const normalizeDeliveryAddress = newDv => {
  if (newDv.delivery_address?.country?.code) {
    newDv.delivery_address.country = newDv.delivery_address.country.code
  }

  if (newDv.delivery_address?.province?.code) {
    newDv.delivery_address.province = newDv.delivery_address.province.code
  }

  if (newDv.delivery_address?.province?.code === null) {
    delete newDv.delivery_address?.province
  }

  delete newDv?.delivery_address?.phone
}

const normalizeExtraFees = (newDv, defaultStoreCurrency) => {
  if (newDv?.extra_fee_additional_transaction === '' || newDv?.extra_fee_additional_transaction === null) {
    newDv.extra_fee_additional_transaction = null
  }

  if (!newDv.extra_fee_type) newDv.extra_fee_type = feeTypes.flatFee
  if (!newDv?.currency) newDv.currency = defaultStoreCurrency

  if (newDv?.extra_fee_type === feeTypes.perReturnedItem) {
    newDv.base_extra_fee = newDv.base_extra_fee || 0
    newDv.base_extra_fee_additional_transaction = newDv.base_extra_fee_additional_transaction || 0
    newDv.extra_fee = newDv.extra_fee || 0
    newDv.extra_fee_additional_transaction = newDv.extra_fee_additional_transaction || 0
    newDv.extra_fee_included_items = newDv.extra_fee_included_items || 0
    newDv.extra_fee_included_items_additional_transaction = newDv.extra_fee_included_items_additional_transaction || 0
  }

  if (newDv?.extra_fee_type === feeTypes.flatFee) {
    delete newDv.base_extra_fee
    delete newDv.base_extra_fee_additional_transaction
    delete newDv.base_extra_fee_included_items
    delete newDv.base_extra_fee_included_items_additional_transaction
  }
}

const normalizeReturnInstructionSet = newDv => {
  delete newDv.return_instructions
  delete newDv.return_instruction_set
}

const normalizeCarrier = (newDv, manageCarrierService) => {
  if (manageCarrierService === manageCarrierServiceTypes.auto) {
    newDv.carrier_service_id = newDv?.carrier_service?.id
  } else if (newDv?.carrier) {
    newDv.carrier_id = newDv?.carrier?.id
  }

  delete newDv?.carrier
  delete newDv?.carrier_service
}

export const getDeliveryMethodPayload = (deliveryMethod, defaultStoreCurrency, manageCarrierService) => {
  const newDv = JSON.parse(JSON.stringify({ ...deliveryMethod }))

  normalizeDeliveryAddress(newDv)
  normalizeExtraFees(newDv, defaultStoreCurrency)

  if (newDv.location_id || newDv?.location?.id) {
    newDv.location_id = newDv?.location_id || newDv?.location?.id
  }

  if (newDv.location_list_type === locationListTypeValues.locations) {
    newDv.url = null
  }

  normalizeReturnInstructionSet(newDv)

  newDv.regions = newDv.regions.map(region => region.id)

  normalizeCarrier(newDv, manageCarrierService)

  delete newDv.availability_criterion_id

  return newDv
}
